import Chart from 'chart.js/auto';
import ChartDataLabelsPlugin from 'chartjs-plugin-datalabels';

import { fetchGalleryImages, resolveUserIdentity } from "./galleryScript.js";

const galleryContainer = document.getElementById('gallery-container');
const statisticsContainer = document.getElementById('statistics-container');
const ageChartCanvas = document.getElementById('age-chart-canvas');
const genderChartCanvas = document.getElementById('gender-chart-canvas');
const localsChartCanvas = document.getElementById('locals-chart-canvas');
const visitorsChartCanvas = document.getElementById('visitors-chart-canvas');
const locationQuestionCanvases = Array.from(
    document.querySelectorAll('canvas[data-location-id][data-question-index]')
);
const participantCountSpan = document.getElementById('participant-count');
const questionText = document.querySelector('.question-text');
const themesContainer = document.querySelector('.themes-scroll-container .scroll-area');
const themesSortButton = document.getElementById('themes-sort-button');
const themesHeaderLabel = document.getElementById('themes-header-label');
const galleryTab = document.querySelector('.tab-container .tab:first-child');
const statisticsTab = document.querySelector('.tab-container .tab:last-child');
const proposeIdeaButton = document.getElementById('gallery-propose-btn');
const locationSelect = document.getElementById('statistics-location-select');
const locationSections = Array.from(document.querySelectorAll('.statistics-location-section'));

let ageData = [
    { ageGroup: "1-14", count: 5 },
    { ageGroup: "15-24", count: 15 },
    { ageGroup: "25-34", count: 20 },
    { ageGroup: "35-44", count: 12 },
    { ageGroup: "45-54", count: 10 },
    { ageGroup: "55-70", count: 20 },
    { ageGroup: "70+", count: 2 }
];

let genderData = [
    { gender: "vrouw", count: 60 },
    { gender: "man", count: 32 },
    { gender: "anders", count: 10 },
    { gender: "ik zeg het liever niet", count: 8 },

];

let placeRelationData = {
    localParticipantsCity: 25,
    localParticipantsNeighbourhood: 45,
    otherVisitors: 10,
};

const questionsData = [
    {
        question: "What's important for you in Groningen?", replies: 26, sentiment: [60, 25, 15],
        themes: [34, 26, 20, 15, 10, 8, 5, 3, 2, 1]
    },
    {
        question: "What do you like about Groningen?", replies: 35, sentiment: [75, 20, 5],
        themes: [19, 35, 3, 9, 10, 8, 15, 3, 2, 4]
    },
    {
        question: "What would you improve in Groningen?", replies: 16, sentiment: [40, 45, 15],
        themes: [30, 25, 20, 11, 10, 5, 3, 2, 5]
    },
];

const themeNames = [
    "Accessibility",
    "Housing",
    "Economy",
    "Leisure",
    "Heritage",
    "Mobility",
    "Biodiversity",
    "Climate",
    "Tourism",
    "Safety"
];

const norm = (s) =>
    (s ?? '')
        .toString()
        .normalize('NFKC')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();

const normalizeLocationId = (value) => {
    const direct = Number(value);
    if (Number.isFinite(direct) && direct > 0) return direct;
    const extracted = parseInt(String(value ?? '').replace(/\D+/g, ''), 10);
    return Number.isFinite(extracted) && extracted > 0 ? extracted : 0;
};


const fallbackParticipationPoints = [
    {
        no: 1,
        Question1: {
            name: "Sfeer",
            answers: [
                "Rust & Natuur",
                "Industrieel aan het water",
                "Actief & sportief",
                "Ontmoeting & samen",
                "Speels & avontuurlijk",
            ]
        },
        Question2: {
            name: "Havenloods",
            answers: [
                "Behouden",
                "Gedeeltelijk slopen",
                "Slopen",
                "Optoppen",
                "Herbestemmen",
            ]
        },
        Question3: {
            name: "Deze plek is bedoelt voor...",
            answers: [
                "Wonen",
                "Creatieve Broedplaats",
                "Horeca",
                "Groen park",
                "Sporten en spelen",
            ]
        }
    },
    {
        no: 2,
        Question1: {
            name: "Sfeer",
            answers: [
                "Rust & Natuur",
                "Industrieel aan het water",
                "Actief & sportief",
                "Ontmoeting & samen",
                "Speels & avontuurlijk",
            ]
        },
        Question2: {
            name: "De kade is bedoelt voor...",
            answers: [
                "Ontdek en leerplek",
                "Horeca paviljoen",
                "Boulevard",
                "zitplek aan het water",
                "Natuurgebied",
            ]
        },
        Question3: {
            name: "Het water is bedoelt voor..",
            answers: [
                "Zitten op het water op steiger",
                "drijvend kantoor",
                "Drijvend park",
                "Aanleg steiger",
                "woonschepen",
            ]
        }
    },
    {
        no: 3,
        Question1: {
            name: "Sfeer",
            answers: [
                "Rust & Natuur",
                "Industrieel aan het water",
                "Actief & sportief",
                "Ontmoeting & samen",
                "Speels & avontuurlijk",
            ]
        },
        Question2: {
            name: "De kade is bedoelt voor...",
            answers: [
                "Sport en spelen",
                "Stadsstrand",
                "Natuurgebied",
                "Wonen aan het water",
                "Horeca",
            ]
        },
        Question3: {
            name: "Het water is bedoelt voor..",
            answers: [
                "Drijvend zwembad",
                "Drijvend park",
                "Historische woonschepen",
                "Drijvende sauna",
                "Watersport",
            ]
        }
    }
];

const getParticipationPoints = () =>
    Array.isArray(window.participationPoints) && window.participationPoints.length
        ? window.participationPoints
        : fallbackParticipationPoints;

const buildQuestionMetaByLocation = (points) => {
    const meta = {};
    points.forEach(point => {
        const locId = Number(point.no);
        if (!Number.isFinite(locId)) return;
        meta[locId] = [point.Question1, point.Question2, point.Question3];
    });
    return meta;
};

const buildAnswerIndexByLocation = (meta) => {
    const indexMap = {};
    Object.keys(meta).forEach(locId => {
        indexMap[locId] = meta[locId].map(question => {
            const map = new Map();
            (question?.answers || []).forEach((answer, idx) => {
                map.set(norm(answer), idx);
            });
            return map;
        });
    });
    return indexMap;
};

const buildEmptyCounts = (meta) => {
    const counts = {};
    Object.keys(meta).forEach(locId => {
        counts[locId] = meta[locId].map(question =>
            new Array((question?.answers || []).length).fill(0)
        );
    });
    return counts;
};

let questionMetaByLocation = buildQuestionMetaByLocation(getParticipationPoints());
let answerIndexByLocation = buildAnswerIndexByLocation(questionMetaByLocation);
let locationQuestionAnswerCounts = buildEmptyCounts(questionMetaByLocation);
let locationQuestionCharts = [];
let representativeImages = [];
let representativeImagesReady = false;
let locationAnswersReady = false;

function refreshQuestionMeta() {
    questionMetaByLocation = buildQuestionMetaByLocation(getParticipationPoints());
    answerIndexByLocation = buildAnswerIndexByLocation(questionMetaByLocation);
}

function parseAnswersField(value) {
    if (value == null) return [];
    if (Array.isArray(value)) {
        return value.map(String).filter(v => v.trim() !== '' && v !== '-');
    }

    const raw = String(value).trim();
    if (!raw || raw === '-') return [];

    if (raw.startsWith('[') && raw.endsWith(']')) {
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                return parsed.map(String).filter(v => v.trim() !== '' && v !== '-');
            }
            if (parsed != null) {
                return [String(parsed)];
            }
        } catch (_err) {
            // fall through to string parsing
        }
    }

    let cleaned = raw;
    if (cleaned.startsWith('[') && cleaned.endsWith(']')) {
        cleaned = cleaned.slice(1, -1);
    }

    cleaned = cleaned.trim();
    if (!cleaned) return [];

    if (cleaned.includes(',')) {
        return cleaned
            .split(',')
            .map(part => part.trim().replace(/^"+|"+$/g, '').replace(/^'+|'+$/g, ''))
            .filter(part => part !== '' && part !== '-');
    }

    const single = cleaned.replace(/^"+|"+$/g, '').replace(/^'+|'+$/g, '').trim();
    return single && single !== '-' ? [single] : [];
}

function getLocationTitleById(locId) {
    const points = getParticipationPoints();
    const point = points.find(p => Number(p.no) === locId);
    return point?.title || point?.name || `Locatie ${locId}`;
}

function getTopAnswersForLocation(locId) {
    const questions = questionMetaByLocation[locId];
    const counts = locationQuestionAnswerCounts[locId];
    if (!questions || !counts) return [];

    return questions.map((question, idx) => {
        const labels = question?.answers || [];
        const values = counts[idx] || [];
        if (!labels.length || !values.length) return '';

        let maxCount = -1;
        let maxIndex = -1;
        values.forEach((value, index) => {
            const count = Number(value) || 0;
            if (count > maxCount) {
                maxCount = count;
                maxIndex = index;
            }
        });

        if (maxIndex < 0 || maxCount <= 0) return '';
        return labels[maxIndex] || '';
    });
}

function getAnswerMatchCount(image, topAnswers) {
    if (!Array.isArray(topAnswers) || topAnswers.length < 3) return 0;

    const answerSets = [
        parseAnswersField(image?.question1_answers),
        parseAnswersField(image?.question2_answers),
        parseAnswersField(image?.question3_answers)
    ].map(list => list.map(norm));

    let matches = 0;
    topAnswers.forEach((answer, idx) => {
        if (!answer) return;
        const normalized = norm(answer);
        if (answerSets[idx] && answerSets[idx].includes(normalized)) {
            matches += 1;
        }
    });
    return matches;
}

function imageMatchesTopAnswers(image, topAnswers) {
    const answerSets = [
        parseAnswersField(image?.question1_answers),
        parseAnswersField(image?.question2_answers),
        parseAnswersField(image?.question3_answers)
    ].map(list => list.map(norm));

    return topAnswers.every((answer, idx) => {
        if (!answer) return false;
        const normalized = norm(answer);
        return answerSets[idx] && answerSets[idx].includes(normalized);
    });
}

function pickBestImageForLocation(locId) {
    const topAnswers = getTopAnswersForLocation(locId);
    const imagesForLocation = representativeImages
        .filter(image => normalizeLocationId(image?.question_id ?? image?.questionId ?? image?.location_id ?? image?.locationId) === locId)
        .filter(image => image?.generated_image && String(image.generated_image).trim() !== '-');

    if (!imagesForLocation.length) return null;

    if (topAnswers.length !== 3 || topAnswers.some(answer => !answer)) {
        return imagesForLocation.sort((a, b) =>
            (Number(b.number_of_likes) || 0) - (Number(a.number_of_likes) || 0)
        )[0];
    }

    const exactMatches = imagesForLocation.filter(image => imageMatchesTopAnswers(image, topAnswers));
    if (exactMatches.length) {
        return exactMatches.sort((a, b) =>
            (Number(b.number_of_likes) || 0) - (Number(a.number_of_likes) || 0)
        )[0];
    }

    const partialMatches = imagesForLocation.filter(image => getAnswerMatchCount(image, topAnswers) >= 2);
    if (partialMatches.length) {
        return partialMatches.sort((a, b) =>
            (Number(b.number_of_likes) || 0) - (Number(a.number_of_likes) || 0)
        )[0];
    }

    return imagesForLocation.sort((a, b) =>
        (Number(b.number_of_likes) || 0) - (Number(a.number_of_likes) || 0)
    )[0];
}

function renderHighlightForLocation(locId, image) {
    const section = locationSections.find(item => normalizeLocationId(item.dataset.locationId) === locId);
    if (!section) return;

    const container = section.querySelector('.statistics-highlight');
    if (!container) return;

    const img = container.querySelector('.statistics-highlight-image');
    const emptyState = container.querySelector('.statistics-highlight-empty');
    const answersContainer = container.querySelector('.statistics-highlight-answers');

    if (image && image.generated_image && String(image.generated_image).trim() !== '-') {
        if (img) {
            img.src = image.generated_image;
            img.alt = `Uitgelicht beeld ${getLocationTitleById(locId)}`;
            img.classList.remove('hidden');
        }
        if (emptyState) {
            emptyState.classList.add('hidden');
        }
        renderHighlightAnswers(answersContainer, locId, image);
        return;
    }

    if (img) {
        img.classList.add('hidden');
    }
    if (emptyState) {
        emptyState.classList.remove('hidden');
    }
    renderHighlightAnswers(answersContainer, locId, null);
}

function renderHighlightAnswers(container, locId, image) {
    if (!container) return;
    container.innerHTML = '';

    if (!image) return;

    const questions = questionMetaByLocation[locId] || [];
    const answerFields = [
        image?.question1_answers,
        image?.question2_answers,
        image?.question3_answers
    ];

    answerFields.forEach((field, idx) => {
        const label = questions[idx]?.name || `Vraag ${idx + 1}`;
        const answers = parseAnswersField(field);
        const value = answers.length ? answers.join(', ') : '-';

        const row = document.createElement('div');
        row.className = 'statistics-highlight-answer';

        const labelSpan = document.createElement('span');
        labelSpan.className = 'statistics-highlight-label';
        labelSpan.textContent = `${label}:`;

        const valueSpan = document.createElement('span');
        valueSpan.textContent = value;

        row.appendChild(labelSpan);
        row.appendChild(valueSpan);
        container.appendChild(row);
    });
}

function updateRepresentativeImages() {
    if (!representativeImagesReady || !locationAnswersReady || !locationSections.length) return;

    locationSections.forEach(section => {
        const locId = normalizeLocationId(section.dataset.locationId);
        if (!locId) return;
        const bestImage = pickBestImageForLocation(locId);
        renderHighlightForLocation(locId, bestImage);
    });
}

function setActiveLocationSection(locId) {
    if (!locationSections.length) return;
    locationSections.forEach(section => {
        const match = normalizeLocationId(section.dataset.locationId) === locId;
        section.classList.toggle('hidden', !match);
    });
}

function initializeLocationSelection() {
    const selected = locationSelect ? normalizeLocationId(locationSelect.value) : 0;
    const fallback = locationSections.length
        ? normalizeLocationId(locationSections[0].dataset.locationId)
        : 0;
    const locId = selected || fallback;
    if (!locId) return;

    if (locationSelect) {
        locationSelect.value = String(locId);
    }
    setActiveLocationSection(locId);
}

function fetchRepresentativeImages() {
    if (typeof resolveUserIdentity !== 'function') return;

    resolveUserIdentity()
        .then(({ ip, uid }) => {
            const url = 'https://publictwin.info/EngagementAppPrototypes/HavenparkGroningenPhase2PHP/Get_Gallery_Images.php' +
                `?ip=${encodeURIComponent(ip || '')}&uid=${encodeURIComponent(uid || '')}`;
            return fetch(url);
        })
        .then(response => response.json())
        .then(data => {
            representativeImages = Array.isArray(data) ? data : [];
            representativeImagesReady = true;
            updateRepresentativeImages();
        })
        .catch(err => console.error('Error fetching representative images:', err));
}


// accepted labels (normalize to be safe)
const NEIGHBOR_LABELS = [
    'bewoner in de buurt van stadshavens',
    'bewoner in de buurt van de stadshavens', // just in case
    'bewoner in de buurt van stadshaven'      // typo variant
].map(norm);

const CITY_LABELS = [
    'inwoner van groningen'
].map(norm);



// A 15% margin above the highest bar to fit the labels
const placeRelationMaxMargin = 0.15;

let placeRelationLocalSum, placeRelationVisitorsSum, participantCount, placeRelationMax;

function calculatePlaceRelationSums() {


    placeRelationLocalSum = placeRelationData.localParticipantsCity + placeRelationData.localParticipantsNeighbourhood;
    placeRelationVisitorsSum = placeRelationData.otherVisitors;
    console.log("placeRelationVisitorsSum:", placeRelationVisitorsSum);

    placeRelationMax = Math.max(placeRelationLocalSum, placeRelationVisitorsSum) * (1 + placeRelationMaxMargin);

}
function calculateGenderRelationSums(genderData) {
    console.log(genderData);
    participantCount = genderData.reduce((sum, row) => sum + row.count, 0);
    console.log(participantCount);
}


calculateGenderRelationSums(genderData)
// calculatePlaceRelationSums();

const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
        duration: 1000,
        easing: 'easeOutCubic',
    }
};

const answerPalette = ['#7CB5EC', '#50527aff', '#FFD700', '#90ED7D', '#F9D235', '#305BAB', '#93A2AE'];
const getAnswerColors = (count) =>
    Array.from({ length: count }, (_, idx) => answerPalette[idx % answerPalette.length]);

const placeRelationChartPlugins = {
    datalabels: {
        labels: {
            value: {},
            topLabel: {}
        },
        color: '#1a1818',
        font: {
            size: 20,
            weight: 'bold'
        },
        value: {
            formatter: function (value, _ctx) {
                // Don't show too small values
                if (value < participantCount * 0.1) {
                    return '';
                }
                return value;
            }
        },
        topLabel: {
            anchor: 'end',
            align: 'top',
            offset: 0,
            formatter: function (_value, ctx) {
                if (ctx.datasetIndex !== 1) {
                    // not the top bar
                    return '';
                }

                if (ctx.chart.canvas === localsChartCanvas)
                    return (placeRelationLocalSum * 100 / participantCount).toFixed(0) + '%';
                else return (placeRelationVisitorsSum * 100 / participantCount).toFixed(0) + '%';
            }
        }
    },
    legend: {
        position: 'bottom',
    },
    title: {
        display: true,
        position: 'bottom',
        align: 'center',
        text: 'Lokale deelnemers',
        color: 'black',
        font: {
            size: 20,
            weight: 'bold'
        },
    }
};

let themes = [];
let themesAscending = false;

function redrawThemes(newThemes) {
    if (newThemes !== undefined) {
        themes = newThemes;
    }

    themesContainer.innerHTML = '';

    themes.sort((a, b) => {
        if (themesAscending) {
            return a.count - b.count;
        }
        return b.count - a.count;
    }).forEach(theme => {
        themesContainer.innerHTML += `
            <div class="theme-row">
                <span class="theme-name">${theme.name}</span>
                <div><span class="theme-count">${theme.count}</span><img src="./res/person.svg" /></div>
            </div>
        `;
    });

    themesHeaderLabel.textContent = themesAscending ? 'Least popular theme: ' : 'Top theme: ';
    themesHeaderLabel.textContent += themes[0].name;
}

let currentTab = null;

function canLaunchParticipation() {
    return typeof window !== 'undefined'
        && typeof window.launchRandomParticipation === 'function';
}

function updateProposeButtonVisibility() {
    if (!proposeIdeaButton) return;
    const shouldShow = currentTab === 'gallery' && canLaunchParticipation();
    proposeIdeaButton.classList.toggle('hidden', !shouldShow);
}

function handleProposeButtonClick() {
    if (!canLaunchParticipation()) return;
    window.launchRandomParticipation();
}

export function selectTab(tabName) {
    currentTab = tabName;
    statisticsTab.classList.remove('selected');
    statisticsContainer.classList.remove('hidden');
    galleryTab.classList.remove('selected');
    galleryContainer.classList.remove('hidden');

    // Update the results menu title based on selected tab
    const resultsTitleSpan = document.querySelector('#ResultsMenu #UrbanFutureChooseRoleTitle .titleBar span');
    if (resultsTitleSpan) {
        resultsTitleSpan.textContent = tabName === 'gallery' ? 'Droombeelden Stadjers' : 'Participatie Resultaten';
    }

    if (tabName === 'statistics') {
        statisticsTab.classList.add('selected');
        galleryContainer.classList.add('hidden');
        resultsInitiated();
    } else if (tabName === 'gallery') {
        galleryTab.classList.add('selected');
        statisticsContainer.classList.add('hidden');
        fetchGalleryImages();
    }

    updateProposeButtonVisibility();
}

if (typeof window !== 'undefined') {
    window.selectResultsTab = selectTab;
}

let charts = [];

function setListeners() {
    const questionButtons = document.querySelectorAll('.question-button');
    questionButtons.forEach((button, index) => {
        button.onclick = () => {
            selectQuestion(index);
        };
    });

    if (themesSortButton !== null) {
        themesSortButton.onclick = () => {
            themesAscending = !themesAscending;
            if (themesAscending) {
                themesSortButton.classList.add('ascending');
            } else {
                themesSortButton.classList.remove('ascending');
            }
            redrawThemes();
        };
    }

    // Select the first question by default
    // selectQuestion(0);

    statisticsTab.onclick = () => {
        selectTab('statistics');
    };
    galleryTab.onclick = () => {
        selectTab('gallery');
    };

    if (proposeIdeaButton) {
        proposeIdeaButton.onclick = handleProposeButtonClick;
    }

    if (locationSelect) {
        locationSelect.onchange = (event) => {
            const locId = normalizeLocationId(event.target.value);
            if (!locId) return;
            setActiveLocationSection(locId);
        };
    }

    updateProposeButtonVisibility();
}

let lastScrollY = statisticsContainer.scrollTop;

function setObservers() {
    const targets = document.querySelectorAll(".canvas-div");

    const observer = new IntersectionObserver((entries) => {
        const scrollingDown = statisticsContainer.scrollTop > lastScrollY;
        lastScrollY = statisticsContainer.scrollTop;

        entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            if (!scrollingDown) return;

            const chart = charts.find(c => c.ctx.canvas.parentElement === entry.target);
            if (!chart) return;

            // Reset and update the chart to trigger the animation
            chart.reset();
            chart.update();
        });
    }, {
        root: null,
        threshold: 0.15
    });

    targets.forEach(div => observer.observe(div));
}

function setUpResults() {
    charts.forEach(chart => chart.destroy());
    charts = [];

    setListeners();
    setObservers();
    initializeLocationSelection();

    refreshQuestionMeta();
    locationQuestionAnswerCounts = buildEmptyCounts(questionMetaByLocation);

    participantCountSpan.textContent = participantCount;

    const ageChart = new Chart(ageChartCanvas, {
        type: 'bar',
        options: {
            ...chartOptions,
            plugins: {
                legend: {
                    display: false
                },
            },
        },
        data: {
            labels: ageData.map(row => row.ageGroup),
            datasets: [
                {
                    label: 'Deelnemers in deze leeftijdsgroep',
                    data: ageData.map(row => row.count),
                    backgroundColor: '#FFD700'
                }
            ]
        }
    });

    const genderChart = new Chart(genderChartCanvas, {
        type: 'pie',
        plugins: [ChartDataLabelsPlugin],
        options: {
            ...chartOptions,
            plugins: {
                datalabels: {
                    formatter: (value, context) => {
                        let percentage = (value / context.chart._metasets
                        [context.datasetIndex].total * 100)
                            .toFixed(0) + '%';
                        return percentage;
                    },
                    color: '#1a1818',
                    font: {
                        size: 17,
                        weight: 'bold'
                    },
                    anchor: 'end',
                    align: 'start',
                    offset: 0,
                }
            }
        },
        data: {
            labels: genderData.map(row => row.gender),
            datasets: [
                {
                    label: 'Deelnemers met dit geslacht',
                    data: genderData.map(row => row.count),
                    backgroundColor: ['#7CB5EC', '#50527aff', '#FFD700', '#90ED7D']
                }
            ]
        }
    });

    const localsChart = new Chart(localsChartCanvas, {
        type: 'bar',
        plugins: [ChartDataLabelsPlugin],
        data: {
            labels: ['Lokale deelnemers'],
            datasets: [
                {
                    label: 'bewoner in de buurt',
                    data: [
                        placeRelationData.localParticipantsNeighbourhood,
                    ],
                    backgroundColor: ['#305BAB']
                },
                {
                    label: 'inwoner van Groningen',
                    data: [
                        placeRelationData.localParticipantsCity,
                    ],
                    backgroundColor: ['#93A2AE']
                },
            ]
        },
        options: {
            ...chartOptions,
            plugins: placeRelationChartPlugins,
            scales: {
                x: {
                    stacked: true,
                    display: false
                },
                y: {
                    stacked: true,
                    max: placeRelationMax,
                    display: false
                },
            }
        }
    });

    const visitorsChart = new Chart(visitorsChartCanvas, {
        type: 'bar',
        plugins: [ChartDataLabelsPlugin],
        data: {
            labels: ['anders'],
            datasets: [
                {
                    label: 'anders',
                    data: [
                        placeRelationData.otherVisitors,
                    ],
                    backgroundColor: ['#F9D235']
                },
            ]
        },
        options: {
            ...chartOptions,
            plugins: {
                ...placeRelationChartPlugins,
                title: {
                    ...placeRelationChartPlugins.title,
                    text: 'Anders',
                }
            },
            scales: {
                x: {
                    stacked: true,
                    display: false
                },
                y: {
                    stacked: true,
                    max: placeRelationMax,
                    display: false
                }
            }
        }
    });

    charts = [
        ageChart,
        genderChart,
        localsChart,
        visitorsChart
    ];

    locationQuestionCharts = locationQuestionCanvases.map(canvas => {
        const locId = Number(canvas.dataset.locationId);
        const questionIndex = Number(canvas.dataset.questionIndex) - 1;
        const question = questionMetaByLocation[locId]?.[questionIndex];
        const labels = question?.answers || [];
        const data = locationQuestionAnswerCounts[locId]?.[questionIndex] || new Array(labels.length).fill(0);

        const chart = new Chart(canvas, {
            type: 'bar',
            plugins: [ChartDataLabelsPlugin],
            data: {
                labels,
                datasets: [{
                    label: question?.name || 'Antwoorden',
                    data,
                    backgroundColor: getAnswerColors(labels.length)
                }]
            },
            options: {
                ...chartOptions,
                plugins: {
                    datalabels: {
                        color: '#1a1818',
                        font: { size: 16, weight: 'bold' },
                        anchor: 'end',
                        align: 'top',
                        offset: 2,
                        clamp: true
                    },
                    legend: { display: false }
                },
                layout: {
                    padding: { top: 24, bottom: 40 }
                },
                scales: {
                    // x: {
                    //     ticks: {
                    //         maxRotation: 45,
                    //         minRotation: 45,
                    //         font: { size: 11 }
                    //     }
                    // },
                    y: {
                        beginAtZero: true,
                        ticks: { precision: 0 }
                    }
                }
            }
        });

        return { locId, questionIndex, chart };
    });

    charts = charts.concat(locationQuestionCharts.map(entry => entry.chart));
}

let firstRender = true;
export function resultsInitiated() {
    if (currentTab === null) {
        // Select statistics tab by default
        selectTab('statistics');
    }
    if (currentTab === 'statistics') {
        // Play animations
        charts.forEach(chart => {
            if (!firstRender) {
                chart.reset();
                chart.update();
                return;
            }

            // Discard initial animation
            chart.ctx.canvas.style.visibility = 'hidden';
            chart.reset();
            chart.update('none');

            setTimeout(() => {
                chart.reset();
                chart.update();
                chart.ctx.canvas.style.visibility = 'visible';
            }, 100);
        });
        firstRender = false;
        return;
    }
    if (currentTab === 'gallery') {
        fetchGalleryImages();
    }
}

let selectedQuestionIndex;
function selectQuestion(index) {
    if (selectedQuestionIndex === index) return;
    if (questionText === null) return;

    const question = questionsData[index];
    questionText.classList.add('disappearing');
    setTimeout(() => {
        questionText.textContent = question.question;
        questionText.classList.remove('disappearing');

        questionText.style.transition = 'none';
        questionText.classList.add('appearing');
        questionText.offsetHeight; // trigger reflow
        questionText.style.transition = '';
        questionText.classList.remove('appearing');
    }, 300);

    const questionButtons = document.querySelectorAll('.question-button');
    questionButtons.forEach(btn => btn.classList.remove('selected'));
    questionButtons[index].classList.add('selected');

    questionButtons[index].querySelector('.reply-count').textContent = question.replies;

    const sentimentPercentages = document.querySelectorAll('.sentiment-percent');
    sentimentPercentages.forEach((span, idx) => {
        span.textContent = question.sentiment[idx] + '%';
    });

    const newThemes = question.themes.map((count, idx) => ({
        name: themeNames[idx],
        count: count
    }));

    redrawThemes(newThemes);

    selectedQuestionIndex = index;
}

function updateChartsData() {
    charts[0].data.datasets[0].data = ageData.map(row => row.count);
    charts[0].update();

    charts[1].data.datasets[0].data = genderData.map(row => row.count);
    charts[1].update();

    charts[2].options.scales.y.max = placeRelationMax;
    charts[2].data.datasets[0].data = [placeRelationData.localParticipantsNeighbourhood];
    charts[2].data.datasets[1].data = [placeRelationData.localParticipantsCity];
    charts[2].update();

    charts[3].options.scales.y.max = placeRelationMax;
    charts[3].data.datasets[0].data = [placeRelationData.otherVisitors];
    charts[3].update();
    locationQuestionCharts.forEach(entry => {
        const question = questionMetaByLocation[entry.locId]?.[entry.questionIndex];
        const labels = question?.answers || [];
        const data = locationQuestionAnswerCounts[entry.locId]?.[entry.questionIndex] || new Array(labels.length).fill(0);

        entry.chart.data.labels = labels;
        entry.chart.data.datasets[0].label = question?.name || 'Antwoorden';
        entry.chart.data.datasets[0].data = data;
        entry.chart.data.datasets[0].backgroundColor = getAnswerColors(labels.length);
        entry.chart.update();
    });

}

// Returns the sum of counts for the given labels in the data array
function getCountFromResults(data, ...labels) {
    const wanted = labels.map(norm);
    return data
        .filter(row => wanted.includes(norm(row.label)))
        .reduce((sum, row) => sum + (parseInt(row.cnt, 10) || 0), 0);
}

function fetchResults() {
    fetch('https://publictwin.info/EngagementAppPrototypes/HavenparkGroningenPhase2PHP/Get_Results.php')
        .then(r => r.json())
        .then(results => {

            const ageDataRows = results.filter(row => row.dataset === 'age');

            const ageGroups = ["1-14", "15-24", "25-34", "35-44", "45-54", "55-70", "70+"];
            const ageGroupLabels = ["1-14", "15-24", "25-34", "35-44", "45-54", "55-70", "70+"];
            ageData = ageGroups.map((group, index) => ({
                ageGroup: ageGroupLabels[index],
                count: getCountFromResults(ageDataRows, group)
            }));

            const genderDataRows = results.filter(row => row.dataset === 'gender');
            genderData = [
                { gender: 'vrouw', count: getCountFromResults(genderDataRows, 'vrouw') },
                { gender: 'man', count: getCountFromResults(genderDataRows, 'man') },
                { gender: 'anders', count: getCountFromResults(genderDataRows, 'anders') },
                { gender: 'ik zeg het liever niet', count: getCountFromResults(genderDataRows, 'ik zeg het liever niet') },

            ];

            console.log("results", results);
            const placeRelationRows = results.filter(row => row.dataset === 'place');
            calculateGenderRelationSums(genderData); // keeps participantCount up to date

            const neighbourhoodCount = getCountFromResults(placeRelationRows, ...NEIGHBOR_LABELS);
            const cityCount = getCountFromResults(placeRelationRows, ...CITY_LABELS);
            const knownLocalTotal = neighbourhoodCount + cityCount;

            placeRelationData = {
                localParticipantsNeighbourhood: neighbourhoodCount,
                localParticipantsCity: cityCount,
                otherVisitors: Math.max(0, participantCount - knownLocalTotal),
            };

            // keep existing calls:
            calculatePlaceRelationSums();
            updateChartsData();
            participantCountSpan.textContent = participantCount;
        })
        .catch(err => console.error(err));
}

function fetchLocationAnswers() {
    fetch('https://publictwin.info/EngagementAppPrototypes/HavenparkGroningenPhase2PHP/Get_Answers.php')
        .then(r => r.json())
        .then(rows => {
            refreshQuestionMeta();
            const counts = buildEmptyCounts(questionMetaByLocation);

            rows.forEach(row => {
                const locId = Number(row.question_id);
                if (!Number.isFinite(locId) || !counts[locId]) return;

                const fields = [
                    row.question1_answers,
                    row.question2_answers,
                    row.question3_answers
                ];

                fields.forEach((field, qIndex) => {
                    const answers = parseAnswersField(field);
                    const indexMap = answerIndexByLocation[locId]?.[qIndex];
                    if (!indexMap) return;

                    answers.forEach(answer => {
                        const idx = indexMap.get(norm(answer));
                        if (idx === undefined) return;
                        counts[locId][qIndex][idx] += 1;
                    });
                });
            });

            locationQuestionAnswerCounts = counts;
            updateChartsData();
            locationAnswersReady = true;
            updateRepresentativeImages();
        })
        .catch(err => console.error(err));
}

setUpResults();
fetchResults();
fetchRepresentativeImages();
setTimeout(fetchLocationAnswers, 0);
